import React from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {useHistory, useLocation} from 'react-router-dom'

import {Box, Grid, Typography} from '@mui/material'
import {makeStyles} from 'tss-react/mui'

import Navigation from '@progyny/pages/Consultations/Program/components/Navigation'
import AppointmentCard from '@progyny/pages/Consultations/components/AppointmentCard'
import CancelConsultation from '@progyny/pages/Consultations/components/CancelConsultation'
import SingleSessionBooking, {
  SessionForBooking,
} from '@progyny/pages/Consultations/components/SingleSession/SingleSessionBooking'
import {
  formatAppointmentDate,
  formatAppointmentTime,
} from '@progyny/utils/appointmentHelpers'

const useStyles = makeStyles()((theme) => ({
  navigation: {
    marginBottom: theme.spacing(8),
  },
}))

export interface AppointmentForTabs {
  id?: string | null
  scheduledTime?: string | null
  programEnrollment?: {program?: {slug?: string | null}} | null
  calcomBookingUid?: string | null
  canReschedule?: boolean | null
  [key: string]: unknown
}

interface SingleSessionTabsProps {
  programSlug: string
  sessionNumber: number
  session: SessionForBooking | null | undefined
  upcomingAppointments: AppointmentForTabs[]
  pastAppointments: AppointmentForTabs[]
  colors: any
  basePath?: string
  showTabs?: boolean
  onBookingSuccessful: () => void
  onCancelBack?: () => void
}

const SingleSessionTabs: React.FC<SingleSessionTabsProps> = ({
  programSlug,
  sessionNumber,
  session,
  upcomingAppointments,
  pastAppointments,
  colors,
  basePath,
  showTabs = true,
  onBookingSuccessful,
  onCancelBack,
}) => {
  const {classes} = useStyles()
  const history = useHistory()
  const location = useLocation()
  const {t} = useTranslation()
  const [activeCancelUrl, setActiveCancelUrl] = React.useState<string | null>(
    null
  )

  const handleCancelBack = React.useCallback(() => {
    setActiveCancelUrl(null)
    if (onCancelBack) {
      onCancelBack()
    }
  }, [onCancelBack])

  const activeNavItem = React.useMemo(() => {
    if (location.pathname.endsWith('/past')) return 'past'
    return 'upcoming'
  }, [location.pathname])

  React.useEffect(() => {
    if (activeNavItem !== 'upcoming' && activeCancelUrl) {
      setActiveCancelUrl(null)
    }
  }, [activeNavItem, activeCancelUrl])

  const navigationItems = [
    {
      itemID: 'upcoming',
      label: t('consultations_program_session_plan_upcoming_consultations'),
    },
    {
      itemID: 'past',
      label: t('consultations_program_session_plan_past_consultations'),
    },
  ]

  const handleNavItemClick = (itemID: string) => {
    const currentBasePath = basePath || location.pathname.replace('/past', '')
    if (itemID === 'past') {
      history.push(`${currentBasePath}/past`)
    } else {
      history.push(currentBasePath)
    }
  }

  const renderTabContent = () => {
    if (activeNavItem === 'past') {
      return (
        <Grid container rowGap={2}>
          {pastAppointments.length > 0 ? (
            pastAppointments.map((appointment, index) => (
              <Grid item xs={12} key={appointment?.id || index}>
                <AppointmentCard
                  status='completed'
                  session={`${t('common_session')} ${sessionNumber}`}
                  title={session?.title || ''}
                  subtitle={session?.subtitle || ''}
                  date={formatAppointmentDate(appointment?.scheduledTime)}
                  time={formatAppointmentTime(appointment?.scheduledTime)}
                  description={session?.description || ''}
                  colors={colors}
                  programSlug={programSlug}
                  sessionNumber={sessionNumber}
                />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography>
                <Trans i18nKey='consultations_single_session_consultation_view_no_past_consultations' />
              </Typography>
            </Grid>
          )}
        </Grid>
      )
    }

    if (activeCancelUrl) {
      return (
        <CancelConsultation
          cancelUrl={activeCancelUrl}
          onBack={handleCancelBack}
        />
      )
    }

    return (
      <Grid container rowGap={2}>
        {upcomingAppointments.length > 0 ? (
          upcomingAppointments.map((appointment, index) => (
            <Grid item xs={12} key={appointment?.id || index}>
              <AppointmentCard
                status='upcoming'
                session={`${t('common_session')} ${sessionNumber}`}
                title={session?.title || ''}
                subtitle={session?.subtitle || ''}
                date={formatAppointmentDate(appointment?.scheduledTime)}
                time={formatAppointmentTime(appointment?.scheduledTime)}
                description={session?.description || ''}
                colors={colors}
                programSlug={programSlug}
                sessionNumber={sessionNumber}
                appointment={
                  appointment
                    ? {
                        id: appointment.id ?? undefined,
                        calcomBookingUid: appointment.calcomBookingUid,
                        canReschedule: appointment.canReschedule,
                      }
                    : null
                }
                onCancelRequested={(url) => setActiveCancelUrl(url)}
              />
            </Grid>
          ))
        ) : (
          <Grid item xs={12} mb={10}>
            {session ? (
              <SingleSessionBooking
                session={session}
                programSlug={programSlug}
                onBookingSuccessful={onBookingSuccessful}
              />
            ) : null}
          </Grid>
        )}
      </Grid>
    )
  }

  return (
    <>
      {showTabs && (
        <Box className={classes.navigation}>
          <Navigation
            items={navigationItems}
            activeItemID={activeNavItem}
            onItemClick={handleNavItemClick}
            colors={colors}
          />
        </Box>
      )}

      {renderTabContent()}
    </>
  )
}

export default SingleSessionTabs
